import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { useTheme } from 'styled-components';
import { A11y, Mousewheel, Navigation, Pagination, Scrollbar } from 'swiper';
import Icon from '../icon';
import { useApplicationSize } from '../../providers/providers-application';
import HorizontalScrollItem from './components/HorizontalScrollItem';
import { getSlidesPerView } from './helpers/getSlidesPerView';
import { useFireEvents } from './hooks/useFireEvents';
import { useHandleResize } from './hooks/useHandleResize';
import { HorizontalScrollProvider } from './provider/HorizontalScrollContext';
import { HorizontalScrollButton, HorizontalScrollContainer, HorizontalScrollWrapper, } from './styles/horizontal-scroll.styled';
import { StyledSwiper } from './styles/swiper.styles';
const HorizontalScroll = forwardRef(function HorizontalScrollWithRef({ children, dataTest = 'horizontal-scroll', slidesPerView: slidesPerViewProp, variant = 'scrollbar', swiperId, ignoreVerticalScroll = false, }, ref) {
    const scrollWrapperRef = useRef(null);
    const theme = useTheme();
    const applicationSize = useApplicationSize();
    const slidesPerView = getSlidesPerView({
        config: slidesPerViewProp,
        applicationSize,
    });
    const spaceBetweenSlides = applicationSize === 'large' ? Number(theme.spacing['400']) : Number(theme.spacing['300']);
    // Refs that connect our custom buttons to Swiper's navigation buttons
    const scrollContainerRef = useRef(null);
    const navigationNextRef = useRef(null);
    const navigationPrevRef = useRef(null);
    useHandleResize({ ref: scrollWrapperRef });
    const { handleProgress, setHasScrolled } = useFireEvents({
        ref: scrollContainerRef,
    });
    const [swiperInstance, setSwiperInstance] = useState(null);
    useImperativeHandle(ref, () => {
        const el = scrollWrapperRef.current;
        if (swiperInstance) {
            el.swiper = swiperInstance;
        }
        return el;
    }, [swiperInstance]);
    /*
        MutationObserver to update Swiper when children are added/removed.
        This is needed as some children may be rendered as client-side components/applications and not be present on initial render.
    */
    useEffect(() => {
        if (!scrollWrapperRef.current || !swiperInstance) {
            return () => { };
        }
        const observer = new MutationObserver(() => {
            swiperInstance.update();
        });
        observer.observe(scrollWrapperRef.current, {
            childList: true,
            subtree: true,
        });
        return () => {
            observer.disconnect();
        };
    }, [scrollWrapperRef, swiperInstance]);
    return (_jsx(HorizontalScrollProvider, { dataTest: dataTest, children: _jsx(HorizontalScrollWrapper, { ref: scrollWrapperRef, children: _jsx(HorizontalScrollContainer, { ref: scrollContainerRef, "data-test": dataTest, children: _jsxs(StyledSwiper, { a11y: { id: swiperId !== null && swiperId !== void 0 ? swiperId : null }, modules: [Navigation, Pagination, Scrollbar, A11y, Mousewheel], mousewheel: {
                        forceToAxis: ignoreVerticalScroll,
                        releaseOnEdges: true,
                    }, navigation: {
                        prevEl: navigationPrevRef.current,
                        nextEl: navigationNextRef.current,
                    }, onSwiper: setSwiperInstance, onScroll: () => {
                        setHasScrolled(true);
                    }, onBeforeInit: (swiper) => {
                        /**
                         * We need to manually set the current button elements to the initial Swiper params to properly connect them
                         */
                        if (!swiper || !swiper.params || !swiper.params.navigation)
                            return;
                        // eslint-disable-next-line no-param-reassign
                        swiper.params.navigation.prevEl = navigationPrevRef.current;
                        // eslint-disable-next-line no-param-reassign
                        swiper.params.navigation.nextEl = navigationNextRef.current;
                    }, pagination: variant === 'pagination' ? { clickable: true } : undefined, scrollbar: variant === 'scrollbar' ? { draggable: true } : undefined, slidesPerView: slidesPerView, spaceBetween: spaceBetweenSlides, watchSlidesProgress: true, onProgress: (e) => {
                        handleProgress(e.progress);
                    }, children: [children, _jsx(HorizontalScrollButton, { "data-test": `${dataTest}-button-prev`, ref: navigationPrevRef, "$position": "left", type: "button", children: _jsx(Icon, { variant: "chevron-left" }) }), _jsx(HorizontalScrollButton, { "data-test": `${dataTest}-button-next`, ref: navigationNextRef, "$position": "right", type: "button", children: _jsx(Icon, { variant: "chevron-right" }) })] }) }) }) }));
});
HorizontalScroll.Item = HorizontalScrollItem;
export default HorizontalScroll;
